import type { DrawingDTO, FighterDTO, SkillId } from '@shared/protocol/messages';
import { RULES } from '../combat/rules';
import type { CombatStats } from '../combat/engine';
import { fitToBudget, statPower } from '../combat/budget';
import { heroClass, itemDef, type ItemSlot } from '../data/presets';
import { newId, type Hero, type StatAlloc } from './model';
import { upgradeBonus } from './upgrade';

/** Upgrade levels of owned items (save.itemLevels). */
type ItemLevels = Readonly<Record<string, number>>;

export function createHero(name: string, classId: string, drawing: DrawingDTO | null): Hero {
  const cls = heroClass(classId);
  return {
    id: newId('h'),
    name: name.trim().slice(0, 24) || cls.name,
    classId: cls.id,
    drawing,
    level: 1,
    exp: 0,
    statPoints: 0,
    alloc: { hp: 0, atk: 0, def: 0, luck: 0 },
    equipment: {},
    skill: cls.skill,
    createdAt: Date.now(),
  };
}

export interface HeroStats {
  maxHp: number;
  atk: number;
  def: number;
  luck: number;
}

const clamp = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));

/** Final stats = class base + allocated points + equipment (with its upgrades), clamped to legal ranges. */
export function heroStats(hero: Hero, levels: ItemLevels = {}): HeroStats {
  const base = heroClass(hero.classId).base;
  const s = { hp: base.hp + hero.alloc.hp * RULES.progression.hpPerPoint, atk: base.atk + hero.alloc.atk, def: base.def + hero.alloc.def, luck: base.luck + hero.alloc.luck };
  for (const slot of Object.keys(hero.equipment) as ItemSlot[]) {
    const item = itemDef(hero.equipment[slot] ?? '');
    if (!item) continue;
    const up = upgradeBonus(item, levels[item.id] ?? 0);
    s.hp += (item.bonus.hp ?? 0) + (up.hp ?? 0);
    s.atk += (item.bonus.atk ?? 0) + (up.atk ?? 0);
    s.def += (item.bonus.def ?? 0) + (up.def ?? 0);
    s.luck += (item.bonus.luck ?? 0) + (up.luck ?? 0);
  }
  const r = RULES.stats;
  return {
    maxHp: clamp(s.hp, r.hp.min, r.hp.max),
    atk: clamp(s.atk, r.atk.min, r.atk.max),
    def: clamp(s.def, r.def.min, r.def.max),
    luck: clamp(s.luck, r.luck.min, r.luck.max),
  };
}

export function heroCombat(hero: Hero, levels: ItemLevels = {}): CombatStats {
  return { name: hero.name, skill: hero.skill, ...heroStats(hero, levels) };
}

/** The atlas art of a hero: its ready-made look, else its class doodle (shown when there is no drawing). */
export function heroArtId(hero: Pick<Hero, 'classId' | 'look'>): string {
  return `hero:${hero.look ?? hero.classId}`;
}

/** The hero's own drawing, unless it wears a ready-made look. */
export function heroDrawing(hero: Pick<Hero, 'drawing' | 'look'>): Hero['drawing'] {
  return hero.look ? null : hero.drawing;
}

/** Loadout for public PvP: scaled into the arena budget so levels can't buy wins. */
export function heroFighter(hero: Hero, mode: 'public' | 'classroom', freeStats: boolean, drawingId?: string, levels: ItemLevels = {}): FighterDTO {
  const st = heroStats(hero, levels);
  let block = { hp: st.maxHp, atk: st.atk, def: st.def, luck: st.luck, skill: hero.skill as SkillId };
  if (!(mode === 'classroom' && freeStats)) block = fitToBudget(block, RULES.budget.pvpPublicBudget);
  return {
    name: hero.name,
    kind: 'hero',
    maxHp: block.hp,
    atk: block.atk,
    def: block.def,
    luck: block.luck,
    skill: block.skill,
    look: hero.look ? { preset: hero.look } : drawingId ? { drawingId, preset: hero.classId } : { preset: hero.classId },
  };
}

export function heroPower(hero: Hero, levels: ItemLevels = {}): number {
  const s = heroStats(hero, levels);
  return statPower({ hp: s.maxHp, atk: s.atk, def: s.def, luck: s.luck, skill: hero.skill });
}

export function expToNext(level: number): number {
  const p = RULES.progression;
  return Math.floor(p.baseExpToLevel * Math.pow(p.expGrowthPercent / 100, level - 1));
}

/** Add EXP, levelling up as needed. Returns the number of levels gained. */
export function grantExp(hero: Hero, exp: number): number {
  const p = RULES.progression;
  let gained = 0;
  hero.exp += Math.max(0, Math.floor(exp));
  while (hero.level < p.maxLevel && hero.exp >= expToNext(hero.level)) {
    hero.exp -= expToNext(hero.level);
    hero.level++;
    hero.statPoints += p.statPointsPerLevel;
    gained++;
  }
  if (hero.level >= p.maxLevel) hero.exp = 0;
  return gained;
}

/** Spend one stat point. Returns false if not allowed. */
export function allocatePoint(hero: Hero, stat: keyof StatAlloc, levels: ItemLevels = {}): boolean {
  if (hero.statPoints <= 0) return false;
  const before = heroStats(hero, levels);
  hero.alloc[stat]++;
  const after = heroStats(hero, levels);
  const key = stat === 'hp' ? 'maxHp' : stat;
  if (after[key] === before[key]) {
    hero.alloc[stat]--;
    return false; // already at the cap
  }
  hero.statPoints--;
  return true;
}

export function equip(hero: Hero, owned: readonly string[], itemId: string): boolean {
  const item = itemDef(itemId);
  if (!item || !owned.includes(itemId)) return false;
  hero.equipment[item.slot] = itemId;
  return true;
}

export function unequip(hero: Hero, slot: ItemSlot): void {
  delete hero.equipment[slot];
}

/** Class skill from level 1; the other skills unlock every two levels from level 3. */
export function skillUnlocks(hero: Hero): { id: SkillId; level: number }[] {
  const cls = heroClass(hero.classId).skill;
  const others = RULES.skills.map((s) => s.id).filter((id) => id !== 'none' && id !== cls);
  return [{ id: cls, level: 1 }, ...others.map((id, i) => ({ id, level: 3 + i * 2 })), { id: 'none' as SkillId, level: 1 }];
}

export function unlockedSkills(hero: Hero): SkillId[] {
  return skillUnlocks(hero)
    .filter((u) => hero.level >= u.level)
    .map((u) => u.id);
}
